<template>
    <div>
        <!-- Show skeleton table when loading stops -->
        <SkeletonTable v-if="isLoading" />

        <!-- Show actual table when stops are loaded -->
        <md-table v-else-if="orderStops.length > 0" class="context-menu-support">
            <md-table-row>
                <TableHeaderDropdown
                    column-icon="keyboard_arrow_down"
                    :default-text="tableColumnOptions.columns[columnKeys.referenceSource].name"
                    :dropdown-options="tableColumnKeyOptions.referenceSource"
                    :selected-option="tableColumnOptions.columns[columnKeys.referenceSource]"
                    @selectedOption="handleColumnSwitch"
                />
                <md-table-head class="address-container">Address</md-table-head>
                <md-table-head>Type</md-table-head>
                <md-table-head>Status</md-table-head>
                <md-table-head>When</md-table-head>
                <md-table-head>Time Window</md-table-head>
                <md-table-head>Assigned To</md-table-head>
                <md-table-head>Duration</md-table-head>
                <md-table-head>Actions</md-table-head>
            </md-table-row>
            <md-table-row v-for="stop in orderStops" :key="stop.stopId">
                <md-table-cell class="stop-reference">
                    <router-link
                        class="ref-link"
                        :to="{
                            name: 'Stop Details',
                            params: { stopId: stop.stopId }
                        }"
                    >
                        {{
                            tableColumnOptions.columns[columnKeys.referenceSource].key === 'reference-number' ||
                            stop.sourceReference === null ||
                            stop.sourceReference === ''
                                ? stop.stopRef
                                : stop.sourceReference
                        }}
                    </router-link>
                </md-table-cell>
                <md-table-cell class="address-container">
                    <div class="custom-ellipsis">
                        <GeocodingIndicator
                            v-if="stop.addressComponents"
                            :address-components="stop.addressComponents"
                        />
                        {{ stop.address || 'No address' }}
                    </div>
                </md-table-cell>
                <md-table-cell>
                    {{ stop.type }}
                </md-table-cell>
                <md-table-cell class="status-container">
                    <StopStatusButton :stop="stop" @statusHistoryUpdated="$emit('statusHistoryUpdated', $event)" />
                </md-table-cell>
                <md-table-cell class="trip-date">
                    <div v-if="stop.tripDate">
                        {{ stop.tripDate | dateFormat(DATE_TYPES.standardDate) }}
                    </div>
                </md-table-cell>
                <md-table-cell class="time-window">
                    <div v-if="stop.timeWindowStart || stop.timeWindowEnd">
                        <span v-if="stop.timeWindowStart">
                            {{ stop.timeWindowStart | dateFormat(DATE_TYPES.standardTime) }}
                        </span>
                        <span v-if="stop.timeWindowStart && stop.timeWindowEnd">-</span>
                        <span v-if="stop.timeWindowEnd">
                            {{ stop.timeWindowEnd | dateFormat(DATE_TYPES.standardTime) }}
                        </span>
                    </div>
                </md-table-cell>
                <md-table-cell>
                    <div v-if="stop.assignedTo" class="assigned-column">
                        <div>
                            <ReassignStopButton
                                type="text"
                                :item="stop"
                                :team-members="teamMembers"
                                :display-availability-indicator="true"
                                @assignedUser="$emit('assignedUser', $event)"
                            />
                        </div>
                    </div>
                    <ReassignStopButton
                        v-else
                        type="button"
                        :item="stop"
                        :team-members="teamMembers"
                        :display-availability-indicator="true"
                        @assignedUser="$emit('assignedUser', $event)"
                    />
                </md-table-cell>
                <md-table-cell>{{ stop.duration }} min</md-table-cell>
                <md-table-cell class="action-buttons">
                    <md-button
                        v-if="stop.trackingLink"
                        class="md-info md-just-icon md-round"
                        title="View Tracking Link"
                        @click.stop="$emit('trackingLinkClick', stop.trackingLink)"
                    >
                        <md-icon>link</md-icon>
                    </md-button>
                    <StopPdfDownloadButton
                        v-if="stop.hasProofOfDelivery"
                        :stop-id="stop.stopId"
                        :status="stop.status.toLowerCase()"
                        :stop-type="stop.type"
                        :stop-ref="stop.stopRef"
                        :use-icon="true"
                    />
                    <CloneJob v-if="canCloneJobs" :stop-id="stop.stopId" :stop-details="stop" :use-icon="true" />
                </md-table-cell>
            </md-table-row>
        </md-table>

        <!-- Show no results message when not loading and no stops -->
        <div v-else>
            <p class="no-result-message">No existing stops for this order.</p>
        </div>
    </div>
</template>

<script>
import { GeocodingIndicator, TableHeaderDropdown, CloneJob } from '@/components';
import { ReassignStopButton, StopStatusButton, StopPdfDownloadButton } from '@/pages/Stops/buttons';
import { DATE_TYPES } from '@/utils/constants';
import SkeletonTable from './SkeletonTable';

export default {
    name: 'StopsTable',
    components: {
        GeocodingIndicator,
        TableHeaderDropdown,
        CloneJob,
        ReassignStopButton,
        StopStatusButton,
        StopPdfDownloadButton,
        SkeletonTable
    },
    props: {
        orderStops: {
            type: Array,
            default: () => []
        },
        isLoading: {
            type: Boolean,
            default: false
        },
        teamMembers: {
            type: Array,
            default: () => []
        },
        tableColumnOptions: {
            type: Object,
            required: true
        },
        columnKeys: {
            type: Object,
            required: true
        },
        tableColumnKeyOptions: {
            type: Object,
            required: true
        },
        canCloneJobs: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            DATE_TYPES
        };
    },
    methods: {
        handleColumnSwitch(event) {
            this.$emit('columnSwitch', event, this.columnKeys.referenceSource, 'orders.defaults.stop-table-options');
        }
    }
};
</script>

<style lang="scss" scoped>
.ref-link {
    color: rgba(0, 0, 0, 0.87) !important;
}

.ref-link:hover {
    color: #2b93ff !important;
}

.stop-reference {
    ::v-deep .md-table-cell-container {
        white-space: normal !important;
        word-wrap: break-word;
        overflow-wrap: break-word;
        line-height: 1.4;
    }
}

/* Stops table styles */
::v-deep .status-container {
    .md-table-cell-container {
        overflow: visible;
    }
}

::v-deep .md-table-cell-container {
    max-width: 205px;

    .md-button.md-round.md-just-icon {
        box-shadow: none;
    }
}

.address-container {
    display: none;
}

@media (min-width: 1400px) {
    .address-container {
        display: table-cell;
        line-height: 30px;
        ::v-deep .md-table-cell-container {
            max-width: 300px;
        }
    }
}

::v-deep .md-table-cell {
    padding: 10px 8px;
    &:last-child {
        .md-table-cell-container {
            text-align: left !important;
            justify-content: flex-start;
        }
    }
}

::v-deep .md-table-row:hover {
    background-color: #fafafa;
    cursor: pointer;
}

::v-deep .md-table-head-label {
    color: #4caf50 !important;
}

.action-buttons {
    ::v-deep .md-table-cell-container {
        display: flex !important;
        align-items: center;
        justify-content: center;
        flex-direction: row;
        gap: 5px;
    }
}

.assigned-column {
    display: flex;
    align-items: center;
}

.custom-ellipsis {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.no-result-message {
    text-align: center;
    color: #666;
    padding: 20px;
    font-style: italic;
}
</style>
